import React from "react";
import {KeenIcon} from "@/components";
import {CommonHexagonBadge} from "@/partials/common";
import {useIntl} from "react-intl";
import {Skill} from "@/pages/buddy/types/MatchDetailsData";
import CharacteristicBadge from "@/components/badges/CharacteristicBadge";
import {Characteristic} from "@/pages/buddy/types/Characteristic.ts";
import {getProgressColor} from "@/pages/buddy/utils/ProgressColor.tsx";

// Helper: define the order for importance and levels for sorting
const importanceOrder = ["Essential", "Desirable", "Optional", "Unspecified"];
const levelOrder = ["Expert", "Professional", "Intermediate", "Amateur", "Novice", "Unspecified"];

// New component: MixedSkillProgressIndicator
// The progress bar is filled according to the job's required skill level and is colored based on the difference between
// the candidate's and job's levels. An icon is positioned on the bar according to the candidate's level.
// All details are available in a tooltip on hover.
interface MixedSkillProgressIndicatorProps {
    characteristicJob: Characteristic;
    characteristicCandidate: Characteristic;
}

const MixedSkillProgressIndicator: React.FC<MixedSkillProgressIndicatorProps> = ({
                                                                                     characteristicJob,
                                                                                     characteristicCandidate,
                                                                                 }) => {
    const jobLevel = characteristicJob.level || "Unspecified";
    const candidateLevel = characteristicCandidate.level || "Unspecified";

    const levelToPercentage = (level: string): number => {
        switch (level) {
            case "Novice": return 20;
            case "Amateur": return 40;
            case "Intermediate": return 60;
            case "Professional": return 80;
            case "Expert": return 100;
            default: return 0;
        }
    };

    const jobPercent = levelToPercentage(jobLevel);
    const candidatePercent = levelToPercentage(candidateLevel);

    const jobIndex = levelOrder.indexOf(jobLevel);
    const candidateIndex = levelOrder.indexOf(candidateLevel);
    const deltaLevels = Math.abs(candidateIndex - jobIndex);

    let diffText = "";
    if (deltaLevels === 0) {
        diffText = "Perfect match";
    } else if (candidateIndex < jobIndex) {
        diffText = `Candidate is ${deltaLevels} level${deltaLevels > 1 ? "s" : ""} higher`;
    } else {
        diffText = `Candidate is ${deltaLevels} level${deltaLevels > 1 ? "s" : ""} lower`;
    }

    let barColor = "#22c55e"; // green
    if (deltaLevels === 1) {
        barColor = "#f59e0b"; // yellow
    } else if (deltaLevels >= 2) {
        barColor = "#ef4444"; // red
    }

    const tooltipText = `Job: ${jobLevel}, Candidate: ${candidateLevel} (${diffText})`;

    const left = Math.min(candidatePercent, jobPercent);
    const width = Math.abs(candidatePercent - jobPercent);

    return (
        <div className="relative mt-2" title={tooltipText}>
            {/* Full gray background bar */}
            <div className="w-full bg-gray-200 rounded-full h-2.5 relative">
                {/* Color-coded bar from job to candidate */}
                <div
                    className="absolute h-2.5 rounded-full"
                    style={{
                        backgroundColor: barColor,
                        left: `${left}%`,
                        width: `${width}%`,
                    }}
                />
            </div>

            {/* Job position icon */}
            <div
                className="absolute"
                style={{
                    left: `${jobPercent}%`,
                    transform: "translateX(-50%)",
                    top: "-7px",
                }}
                title={`Job: ${jobLevel}`}
            >
                <div className="bg-[#F39200] rounded-full w-6 h-6 flex items-center justify-center">
                    <KeenIcon
                        icon="teacher"
                        style="solid"
                        className="w-4 h-4.5 text-white"
                    />
                </div>
            </div>

            {/* Candidate position icon */}
            <div
                className="absolute"
                style={{
                    left: `${candidatePercent}%`,
                    transform: "translateX(-50%)",
                    top: "-7px",
                }}
                title={`Candidate: ${candidateLevel}`}
            >
                <div className="bg-[#F39200] rounded-full w-6 h-6 flex items-center justify-center">
                    <KeenIcon
                        icon="user"
                        style="solid"
                        className="w-4 h-4.5 text-white"
                    />
                </div>
            </div>
        </div>
    );
};

export default MixedSkillProgressIndicator;

interface SkillsComparisonProps {
    matchId: string;
    onRefresh: () => Promise<void>;
    criterion: string;
    matched_skills: Skill[];
    missing_skills: Skill[];
    additional_skills: Skill[];
}

export const SkillsComparison = ({
                                     matchId,
                                     onRefresh,
                                     criterion,
                                     matched_skills,
                                     missing_skills,
                                     additional_skills,
                                 }: SkillsComparisonProps) => {
    const intl = useIntl();

    // Sort matched skills by job importance then job level
    const sortedMatchedSkills = matched_skills.sort((a, b) => {
        const aImportance = importanceOrder.indexOf(a.job_characteristic.importance || "Unspecified");
        const bImportance = importanceOrder.indexOf(b.job_characteristic.importance || "Unspecified");
        if (aImportance !== bImportance) return aImportance - bImportance;
        const aLevel = levelOrder.indexOf(a.job_characteristic.level || "Unspecified");
        const bLevel = levelOrder.indexOf(b.job_characteristic.level || "Unspecified");
        return aLevel - bLevel;
    });

    // Sort missing skills (job-related) by importance then level
    const sortedMissingSkills = missing_skills.sort((a, b) => {
        const aImportance = importanceOrder.indexOf(a.job_characteristic.importance || "Unspecified");
        const bImportance = importanceOrder.indexOf(b.job_characteristic.importance || "Unspecified");
        if (aImportance !== bImportance) return aImportance - bImportance;
        const aLevel = levelOrder.indexOf(a.job_characteristic.level || "Unspecified");
        const bLevel = levelOrder.indexOf(b.job_characteristic.level || "Unspecified");
        return aLevel - bLevel;
    });

    // Sort additional skills (candidate-related) by candidate level
    const sortedAdditionalSkills = additional_skills.sort((a, b) => {
        const aLevel = levelOrder.indexOf(a.candidate_characteristic.level || "Unspecified");
        const bLevel = levelOrder.indexOf(b.candidate_characteristic.level || "Unspecified");
        return aLevel - bLevel;
    });

    return (
        <div>
            <div className="grid grid-cols-2 gap-4">
                {/* Matched Skills (badge and progress indicator pair) */}
                <div>
                    <p className="text-sm mb-1 font-bold">
                        {intl.formatMessage({id: "matched_skills"})}
                    </p>
                    {sortedMatchedSkills.map((skill, index) => (
                        <div key={`skill-${index}`} className="grid grid-cols-[auto_1fr] gap-2 mb-2.5">
                            {/* Fixed width for badge */}
                            <div>
                                <CharacteristicBadge
                                    entity="match"
                                    characteristicJob={skill.job_characteristic}
                                    characteristicCandidate={skill.candidate_characteristic}
                                    skill={skill}
                                    matchId={matchId}
                                    criterion={criterion}
                                    onRefresh={onRefresh}

                                />
                            </div>
                            {/* Progress indicator fills the rest; right padding added for spacing */}
                            <div className="flex-1 pr-8 pl-8">
                                <MixedSkillProgressIndicator
                                    characteristicJob={skill.job_characteristic}
                                    characteristicCandidate={skill.candidate_characteristic}
                                />
                            </div>
                        </div>
                    ))}
                </div>

                {/* Missing Skills (badges, displayed in one row if space allows) */}
                <div>
                    <p className="text-sm mb-1 font-bold">
                        {intl.formatMessage({id: "missing_skills"})}
                    </p>
                    <div className="flex flex-wrap gap-2.5">
                        {sortedMissingSkills.map((skill, index) => (
                            <CharacteristicBadge
                                key={index}
                                entity="job"
                                characteristicJob={skill.job_characteristic}
                                skill={skill}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
                {/* Additional Skills (badges, displayed in one row if space allows) */}
                <div>
                    <p className="text-sm mb-1 font-bold">
                        {intl.formatMessage({id: "additional_skills"})}
                    </p>
                    <div className="flex flex-wrap gap-2.5">
                        {sortedAdditionalSkills.map((skill, index) => (
                            <CharacteristicBadge
                                key={index}
                                entity="candidate"
                                characteristicCandidate={skill.candidate_characteristic}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

interface SkillCriterionProps {
    matchId: string;                       // NEW
    onRefresh: () => Promise<void>;        // NEW
    criterionName: string;
    criterionDetail: {
        matched_skills: Skill[];
        missing_skills: Skill[];
        additional_skills: Skill[];
        soft_score_absolute: number;
        soft_score_absolute_max: number;
        soft_score_relative: number;
        is_reliable: boolean;
        explanation: string;
        skill_gap_explanation?: string;
    };
}

export const SkillCriterion = ({
                                   matchId,
                                   onRefresh,
                                   criterionName,
                                   criterionDetail,
                               }: SkillCriterionProps) => {
    const progressPercentage = Math.round(criterionDetail.soft_score_relative * 100);
    const intl = useIntl();

    return (
        <div className="card">
            <div className="card-header flex justify-between items-center">
                <h3 className="card-title">
                    {intl.formatMessage({id: criterionName})}
                </h3>
            </div>

            <div className="card-body">
                <div className="mb-4">
                    <div className="text-sm font-bold mb-1">
                        {criterionDetail.soft_score_absolute}/{criterionDetail.soft_score_absolute_max} ({intl.formatMessage({id: "score"})}: {progressPercentage}%)
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                            className="h-2.5 rounded-full"
                            style={{
                                width: `${progressPercentage}%`,
                                backgroundColor: getProgressColor(progressPercentage),
                            }}
                        ></div>
                    </div>
                </div>

                <SkillsComparison
                    matchId={matchId}
                    onRefresh={onRefresh}
                    criterion={criterionName}
                    matched_skills={criterionDetail.matched_skills || []}
                    missing_skills={criterionDetail.missing_skills || []}
                    additional_skills={criterionDetail.additional_skills || []}
                />
            </div>

            {criterionDetail.skill_gap_explanation && (
                <div className="card-footer justify-center">
                    <div className="flex items-center gap-4">
            <span>
              <CommonHexagonBadge
                  stroke="stroke-success-clarity"
                  fill="fill-success-light"
                  size="size-[40px]"
                  badge={
                      <KeenIcon
                          icon="abstract-23"
                          className="text-success text-1xl ps-px"
                      />
                  }
              />
            </span>
                        <div className="text-sm">
                            {criterionDetail.skill_gap_explanation}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
